/*
 * Dashboard Call History
 *
 * - Holds the most recent inbound, outbound and missed calls
 * - Holds a counter for number of missed calls since last outbound/answered call
 */

import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useSettingsStore } from '@/stores/Settings'
import { useRealtimeStore } from '@/stores/Realtime'
import { useApiStore } from '@/stores/Api'
import { usePhonebookStore } from '@/stores/Phonebook'
import { useEmployeesStore } from '@/stores/Employees'

import globalEvents from '@/utilities/GlobalEvents'
import logger from '@/utilities/Logger'

import { type PbxEventCall } from '@per_moeller/tcx-realtime-client'
import { type CallHistorySession, type CallHistoryTransferInProgressItem } from './CallHistory.types'
import { type PbxCdrLatestResponse, type PbxCdrLatestCallRecord } from '@/TcxApi/PbxCdr'

export const useCallHistoryStore = defineStore('callHistory', () => {
	logger.debug('CallHistory: Initializing store')

	const ss = useSettingsStore()
	const realtime = useRealtimeStore()
	const api = useApiStore()
	const employees = useEmployeesStore()
	const Phonebook = usePhonebookStore()

	/** Current calls on the extension */
	const sessions = ref<Record<string, CallHistorySession>>({})

	/** Number of missed calls */
	const missedCallsCount = ref(0)

	/** List of eventIds for call legs that are being transferred (= not not count as missed as we transferred the call)  */
	const transfersInProgress = ref<CallHistoryTransferInProgressItem[]>([])

	/** Call history - last 10 calls */
	const outbound = ref<PbxCdrLatestCallRecord[]>([])
	const inbound = ref<PbxCdrLatestCallRecord[]>([])
	const missed = ref<PbxCdrLatestCallRecord[]>([])

	realtime.realtimeEvents.on('Connected', load)
	realtime.pbxEvents.on('CALL_START', callStart)
	realtime.pbxEvents.on('CALL_ANSWER', callAnswer)
	realtime.pbxEvents.on('CALL_END', callEnd)
	globalEvents.on('AttendedTransferRequested', transferRequested)
	globalEvents.on('BlindTransferRequested', transferRequested)
	globalEvents.on('CallRejected', transferRequested)
	globalEvents.on('OneSecond', cleanup)

	if (realtime.connected) { load() } // otherwise it is invoked on Realtime:Connected

	/**
	 * Cleanup that runs every second on transferInProgress (after 5 seconds they are removed)
	 */
	function cleanup() {
		if (transfersInProgress.value.length === 0) { return }
		const cutOff = new Date()

		cutOff.setSeconds(cutOff.getSeconds() - 5)
		transfersInProgress.value = transfersInProgress.value.filter(item => item.created < cutOff)
	}

	/**
	 * Register that call transfer for a call leg is in progress (so that we do not count the call as missed)
	 * Also handles when we activly reject a call
	 * @param data Transfer object
	 */
	function transferRequested(data: { source: string }) {
		transfersInProgress.value.push({ callId: data.source, created: new Date() })
	}

	/**
	 * Load latest 10 missed, inbound and outbound calls on the extension
	 */
	async function load() {
		try {
			await ss.ready()
			await employees.ready()

			const res = await api.get<PbxCdrLatestResponse>(`/pbx/cdr/latest?extension=${employees.me?.primaryExtension?._id}`)

			for (const type of ['inbound', 'outbound', 'missed']) {
				for (const call of res.data[type as keyof typeof res.data]) {
					call.started = new Date(call.started)
					if (call.answered) { call.answered = new Date(call.answered) }
					if (call.ended) { call.ended = new Date(call.ended) }
					if (!call.caller.name) {
						const emp = employees.findEmployeeFromNumber(call.caller.number)
						if (emp) { call.caller.name = emp.name }
						else {
							const name = Phonebook.findNameFromNumber(call.caller.number)
							if (name) { call.caller.name = name }
						}
					}
					if (!call.callee.name) {
						const emp = employees.findEmployeeFromNumber(call.callee.number)
						if (emp) { call.callee.name = emp.name }
						else {
							const name = Phonebook.findNameFromNumber(call.callee.number)
							if (name) { call.callee.name = name }
						}
					}
				}
			}
			outbound.value = res.data.outbound
			inbound.value = res.data.inbound
			missed.value = res.data.missed
		}
		catch (_) { /* dont care */ }
	}

	/**
	 * Invoked when a call starts on the customer this extension belongs to
	 * @param event Call event object
	 */
	function callStart(event: PbxEventCall) {
		if (!employees.me || event.extension !== employees.me.primaryExtension?._id) { return }

		if (event.leg === 'O') { // I receive the call (O=out of PBX)
			// We only create session for calls we receive, as that is all we want to count missed calls
			let session = sessions.value[event.sessionId]
			if (!session) {
				session = { started: new Date(), devices: {} }
				sessions.value[event.sessionId] = session
			}
			if (event.device) {
				session.devices[event.device] = 'STARTED'
			}
			// Don't overwrite a previously-set queueId with undefined — later legs of the same
			// session (ring group, fallback leg, simultaneous ring) may omit it.
			if (event.queueId) { session.queueId = event.queueId }
		}
		if (event.leg === 'I' && event.device === ss.realPhoneId) { // I made the call from this communicator (I=into PBX)
			missedCallsCount.value = 0 // reset counter - i started a call
		}
	}

	/**
	 * Invoked when a call is answered on the customer this extension belongs to
	 * @param event Call event object
	 */
	function callAnswer(event: PbxEventCall) {
		if (!employees.me || event.extension !== employees.me.primaryExtension?._id) { return }

		if (event.leg === 'O') {
			let session = sessions.value[event.sessionId]
			if (!session) {
				session = { started: new Date(), devices: {} }
				sessions.value[event.sessionId] = session
			}
			if (event.device) {
				session.devices[event.device] = 'ANSWERED'
			}
			session.answered = new Date()
			if (event.queueId) { session.queueId = event.queueId }
		}
		missedCallsCount.value = 0 // If a call is answered in or out we reset the counter
	}

	/**
	 * Invoked when a call has ended on the customer this extension belongs to
	 * @param event Call event object
	 */
	function callEnd(event: PbxEventCall) {
		if (!employees.me || event.extension !== employees.me.primaryExtension?._id) { return }

		// Call has ended
		// If not answered add to unanswered count
		// If we received the call, it has not been answered and is not from a queue
		const session = sessions.value[event.sessionId]

		// If I received the call, we have a session for it, and it was not a queue call....
		if (event.leg === 'O' && session && !session.queueId) {
			// If we did not answer the call, and this call event is for the last device the call was sent to...
			if (!session.answered && Object.keys(session.devices).length === 1) {
				// ... and is not currently being transferred
				const inTransfer = transfersInProgress.value.find(item => item.callId === event.eventId)
				if (!inTransfer) {
					missedCallsCount.value++ // Then it is a missed call
				}
			}
		}

		// If we have a session for this call event
		if (session) {
			if (event.device) {
				delete session.devices[event.device] // eslint-disable-line
			}
			if (Object.keys(session.devices).length === 0) {
				// No more devices, delete the session and load the calls
				delete sessions.value[event.sessionId] // eslint-disable-line
				load() // Inbound call has ended, the last of the called devices has left - so we need to load to get the latest call
			}
		} else {
			// We are not tracking the call, because it is an outbound call - but we still need to update
			load() // Outbound call has ended - we we need to load to get the latest call
		}
	}

	return { missedCallsCount, outbound, inbound, missed }
})
